/**
 * Sistema de Notificaciones Push para GENIO
 * Compatible con Vue 2 + TypeScript + Decoradores
 *
 * Soporte completo para:
 * - Chrome ✅
 * - Firefox ✅
 * - Opera ✅
 * - Edge ✅
 * - Safari ✅
 *
 * @author GENIO Team
 * @version 1.0.0
 */

/**
 * Verifica si el navegador soporta notificaciones
 */
export function isNotificationSupported (): boolean {
  return 'Notification' in window && 'serviceWorker' in navigator
}

/**
 * Obtiene el estado actual del permiso de notificaciones
 */
export function getNotificationPermission (): NotificationPermission {
  return Notification.permission
}

/**
 * Parsea la versión del navegador desde el user agent
 */
function parseVersion (userAgent: string, pattern: RegExp): string {
  const match = userAgent.match(pattern)
  return match ? match[1] : 'unknown'
}

/**
 * Determina el nombre y versión del navegador
 */
function getBrowserInfo (userAgent: string): { name: string, version: string } {
  // IMPORTANTE: Opera debe verificarse ANTES que Chrome porque incluye "Chrome" en su UA
  if (userAgent.includes('OPR') || userAgent.includes('Opera')) {
    return {
      name: 'opera',
      version: parseVersion(userAgent, /(?:OPR|Opera)\/(\d+)/),
    }
  }

  if (userAgent.includes('Edg')) {
    return {
      name: 'edge',
      version: parseVersion(userAgent, /Edg\/(\d+)/),
    }
  }

  if (userAgent.includes('Firefox')) {
    return {
      name: 'firefox',
      version: parseVersion(userAgent, /Firefox\/(\d+)/),
    }
  }

  if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    return {
      name: 'safari',
      version: parseVersion(userAgent, /Version\/(\d+)/),
    }
  }

  if (userAgent.includes('Chrome')) {
    // Chrome debe ser el ÚLTIMO porque otros navegadores incluyen "Chrome"
    return {
      name: 'chrome',
      version: parseVersion(userAgent, /Chrome\/(\d+)/),
    }
  }

  return { name: 'unknown', version: 'unknown' }
}

/**
 * Detecta el navegador actual
 */
export function detectBrowser (): {
  name: string
  version: string
  isSupported: boolean
  } {
  const userAgent = navigator.userAgent
  const { name, version } = getBrowserInfo(userAgent)

  // Verificar soporte específico por navegador
  let isSupported = true
  if (name === 'edge' && parseInt(version) < 79) {
    isSupported = false // Edge legacy no soporta bien las notificaciones
  }

  return { name, version, isSupported }
}

/**
 * Maneja la solicitud de permiso específica para Edge
 */
async function requestEdgePermission (): Promise<NotificationPermission> {
  if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
    throw new Error('Edge requires HTTPS')
  }

  const timeoutPromise = new Promise<NotificationPermission>((resolve, reject) => {
    setTimeout(() => reject(new Error('Timeout')), 10000)
  })

  const permissionPromise = Notification.requestPermission()

  try {
    return await Promise.race([permissionPromise, timeoutPromise])
  } catch (error) {
    return new Promise<NotificationPermission>(resolve => {
      if (Notification.permission !== 'default') {
        resolve(Notification.permission)
      } else {
        setTimeout(() => {
          resolve(Notification.requestPermission())
        }, 500)
      }
    })
  }
}

/**
 * Maneja la solicitud de permiso específica para Firefox
 */
async function requestFirefoxPermission (): Promise<NotificationPermission> {
  if (Notification.permission === 'default') {
    const permission = await Notification.requestPermission()

    if (permission === 'granted') {
      // Pequeño delay para que Firefox procese el permiso
      await new Promise(resolve => setTimeout(resolve, 100))
    }

    return permission
  }

  return Notification.permission
}

/**
 * Solicita permiso para mostrar notificaciones con manejo específico por navegador
 */
export async function requestNotificationPermission (): Promise<string> {
  if (!isNotificationSupported()) {
    return 'unsupported'
  }

  const browser = detectBrowser()

  try {
    let permission: NotificationPermission

    if (browser.name === 'edge') {
      permission = await requestEdgePermission()
    } else if (browser.name === 'firefox') {
      permission = await requestFirefoxPermission()
    } else {
      permission = await Notification.requestPermission()
    }

    return permission
  } catch (error) {
    return 'denied'
  }
}

/**
 * Crea las opciones de notificación según el navegador
 */
function createNotificationOptions (
  title: string,
  body: string,
  browser: { name: string, version: string },
  options?: { icon?: string, tag?: string, url?: string, data?: any }
): any {
  const baseOptions = {
    body,
    icon: options?.icon || '/img/icons/android-chrome-192x192.png',
    badge: '/img/icons/android-chrome-192x192.png',
    tag: options?.tag || 'genio-notification',
    data: {
      url: options?.url || '/',
      timestamp: Date.now(),
      source: 'local',
      ...options?.data,
    },
  }

  // Configuraciones específicas por navegador
  if (browser.name === 'chrome') {
    return {
      ...baseOptions,
      requireInteraction: true,
      actions: [
        { action: 'view', title: 'Ver' },
        { action: 'dismiss', title: 'Descartar' },
      ],
    }
  }

  if (browser.name === 'firefox') {
    return {
      ...baseOptions,
      silent: false,
      requireInteraction: false,
      dir: 'ltr',
    }
  }

  if (browser.name === 'opera') {
    return {
      ...baseOptions,
      requireInteraction: false,
      silent: false,
      renotify: true,
    }
  }

  if (browser.name === 'edge') {
    return {
      ...baseOptions,
      requireInteraction: true,
      renotify: true,
    }
  }

  if (browser.name === 'safari') {
    return {
      ...baseOptions,
      silent: false,
    }
  }

  return baseOptions
}

/**
 * Intenta mostrar notificación via Service Worker
 */
async function tryServiceWorkerNotification (
  options: any,
  browser: { name: string, version: string }
): Promise<boolean> {
  try {
    if ('serviceWorker' in navigator) {
      const registration = await navigator.serviceWorker.ready
      if (registration && registration.showNotification) {
        await registration.showNotification(options.title || 'GENIO', options)
        return true
      }
    }
  } catch (error) {
    // Continuar con método básico
  }
  return false
}

/**
 * Muestra notificación básica usando Notification API
 */
function showBasicNotification (
  options: any,
  browser: { name: string, version: string }
): boolean {
  try {
    const notification = new Notification(options.title || 'GENIO', options)

    notification.onclick = () => {
      // Enfocar la ventana
      window.focus()

      // Navegar a la URL si está disponible
      if (options.data?.url && options.data.url !== '/') {
        // Si es una URL externa, abrir en nueva ventana
        if (options.data.url.startsWith('http')) {
          window.open(options.data.url, '_blank')
        } else {
          // Si es una ruta interna, navegar en la misma ventana
          window.location.href = options.data.url
        }
      }

      notification.close()
    }

    // Auto-cerrar para Firefox y Opera
    if (browser.name === 'firefox' || browser.name === 'opera') {
      setTimeout(() => {
        notification.close()
      }, 5000)
    }

    return true
  } catch (error) {
    return false
  }
}

/**
 * Muestra una notificación local con opciones personalizadas
 */
export async function showLocalNotification (options: {
  title: string
  body?: string
  icon?: string
  tag?: string
  url?: string
  data?: any
}): Promise<boolean> {
  if (!isNotificationSupported() || Notification.permission !== 'granted') {
    return false
  }

  const browser = detectBrowser()
  const notificationOptions = createNotificationOptions(
    options.title,
    options.body || '',
    browser,
    {
      icon: options.icon,
      tag: options.tag,
      url: options.url,
      data: options.data,
    }
  )

  // Intentar Service Worker primero
  const swSuccess = await tryServiceWorkerNotification(
    { ...notificationOptions, title: options.title },
    browser
  )

  if (swSuccess) {
    return true
  }

  // Fallback a notificación básica
  return showBasicNotification(
    { ...notificationOptions, title: options.title },
    browser
  )
}

/**
 * Muestra una notificación de prueba
 */
export async function showTestNotification (): Promise<boolean> {
  return showLocalNotification({
    title: '🎉 ¡Notificación de GENIO!',
    body: 'Las notificaciones están funcionando correctamente en tu navegador.',
    tag: 'genio-test',
    url: '/dashboard', // URL de ejemplo para testing
  })
}

/**
 * Inicializa el sistema de notificaciones
 */
export async function initializeNotifications (): Promise<{
  supported: boolean
  permission: NotificationPermission
  ready: boolean
  browser: { name: string, version: string, isSupported: boolean }
}> {
  const browser = detectBrowser()
  const supported = isNotificationSupported()
  const permission = getNotificationPermission()
  const ready = supported && permission === 'granted'

  // Exponer funciones de testing en desarrollo
  if (process.env.NODE_ENV === 'development') {
    exposeTestingFunctions()
  }

  return {
    supported,
    permission,
    ready,
    browser,
  }
}

/**
 * Procesa notificaciones push desde el backend
 */
export function handleBackendPushNotification (data: {
  title: string
  body: string
  icon?: string
  url?: string
  actions?: Array<{ action: string, title: string }>
}): Promise<boolean> {
  return showLocalNotification({
    title: data.title,
    body: data.body,
    icon: data.icon,
    data: {
      url: data.url,
      source: 'backend',
      timestamp: Date.now(),
    },
  })
}

/**
 * Expone funciones para testing desde la consola del navegador
 */
export function exposeTestingFunctions (): void {
  if (!(window as any).genioNotifications) {
    (window as any).genioNotifications = {}
  }

  (window as any).genioNotifications = {
    test: showTestNotification,
    debug: () => {
      const browser = detectBrowser()
      const permission = Notification.permission
      const supported = isNotificationSupported()

      return {
        browser,
        permission,
        supported,
        serviceWorker: !!navigator.serviceWorker,
        userAgent: navigator.userAgent,
      }
    },
    browser: detectBrowser,
    permission: () => Notification.permission,
    request: requestNotificationPermission,
    show: showLocalNotification,
    backend: handleBackendPushNotification,
  }
}
